import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useRef, useState, useMemo, useCallback } from 'react';
import useEffectOnce from 'react-use/lib/useEffectOnce';
import useDebounce from 'react-use/lib/useDebounce';
import { useSdk } from '@packlink/packlink-sdk-provider';
import { useTranslation } from '@packlink/translation-provider';
import { PostalCodesRepository } from '@packlink/packlink-sdk';
import { FieldMessageType, FormField, SelectAutocomplete } from '@shipengine/giger';
import { jsx } from '@emotion/react/jsx-runtime';
import { useSelectAutocompleteMessages } from '../hooks/useSelectAutocompleteMessages.js';

const QUERY_KEY = 'postalCodeSelector';
const PostalCodeSelector = _ref => {
  let {
    alpha2Code,
    errorMessage,
    hintMessage,
    isInvalid,
    name,
    value,
    labelTranslationKey,
    onChange: onChangeProps,
    onLoad,
    ...rest
  } = _ref;
  const sdk = useSdk();
  const {
    t
  } = useTranslation();
  const queryClient = useQueryClient();
  const selectorRef = useRef({
    isFirstRender: true
  });
  const selectAutocompleteMessages = useSelectAutocompleteMessages('postalCode', labelTranslationKey);
  const [selectAutocompleteLabel, setSelectAutocompleteLabel] = useState(selectAutocompleteMessages.start);
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [queryValue, setQueryValue] = useState((value === null || value === void 0 ? void 0 : value.label) || '');
  const PostalCodesRepository$1 = useMemo(() => new PostalCodesRepository(sdk.apiClient), [sdk.apiClient]);
  const {
    data: postalCodes
  } = useQuery({
    queryKey: [QUERY_KEY, queryValue, alpha2Code],
    queryFn: () => PostalCodesRepository$1.allByAlpha2Code(alpha2Code, {
      q: queryValue
    }).then(postalCodeResponse => {
      if (selectorRef.current.isFirstRender && onLoad && value) {
        onLoad(postalCodeResponse === null || postalCodeResponse === void 0 ? void 0 : postalCodeResponse[0]);
        selectorRef.current.isFirstRender = false;
      }
      if (!(postalCodeResponse !== null && postalCodeResponse !== void 0 && postalCodeResponse.length)) {
        setSelectAutocompleteLabel(selectAutocompleteMessages.noResults);
      }
      return postalCodeResponse;
    }),
    enabled: !!alpha2Code && !!queryValue,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false
  });
  const postalCodesOptions = useMemo(() => (postalCodes === null || postalCodes === void 0 ? void 0 : postalCodes.map(transformPostalCodeToPostalCodeOption)) || [], [postalCodes]);

  /**
   * With the first render lets invalidate the query to fetch the initial postal code
   */
  useEffectOnce(() => {
    if (onLoad && value) {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY]
      });
    }
  });
  useDebounce(() => {
    if (!isFocused || !inputValue) {
      return;
    }
    setQueryValue(inputValue);
  }, 500, [inputValue, isFocused]);
  const onInputChange = _ref2 => {
    let {
      target: {
        value
      }
    } = _ref2;
    setInputValue(value);
    setSelectAutocompleteLabel(value ? selectAutocompleteMessages.loading : selectAutocompleteMessages.start);
  };
  const onChange = useCallback((name, value) => {
    if (!value) {
      setSelectAutocompleteLabel(selectAutocompleteMessages.start);
    }
    const selectedPostalCode = postalCodes === null || postalCodes === void 0 ? void 0 : postalCodes.find(postalCodeOption => postalCodeOption.id === (value === null || value === void 0 ? void 0 : value.value));
    onChangeProps(name, value, selectedPostalCode || null);
  }, [onChangeProps, postalCodes, selectAutocompleteMessages.start]);
  const formFieldMessage = useMemo(() => {
    if (hintMessage && !isInvalid) {
      return {
        type: FieldMessageType.HINT,
        content: hintMessage
      };
    } else if (isInvalid && errorMessage) {
      return {
        type: FieldMessageType.ERROR,
        content: errorMessage
      };
    }
    return undefined;
  }, [errorMessage, hintMessage, isInvalid]);
  return jsx(FormField, {
    message: formFieldMessage,
    children: jsx(SelectAutocomplete, {
      name: name,
      value: value,
      isInvalid: isInvalid,
      disabled: !alpha2Code,
      options: postalCodesOptions,
      noResultsLabel: selectAutocompleteLabel,
      label: labelTranslationKey ? t(labelTranslationKey) : t('address-selectors.postal-code.label'),
      onChange: onChange,
      onInputChange: onInputChange,
      onFocus: () => !isFocused && setIsFocused(true),
      ...rest
    })
  });
};
function transformPostalCodeToPostalCodeOption(postalCode) {
  return {
    label: postalCode.text,
    value: postalCode.id
  };
}

export { PostalCodeSelector, transformPostalCodeToPostalCodeOption };
//# sourceMappingURL=PostalCodeSelector.js.map
